import { useEffect, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowUpRight, ChevronDown, RefreshCw, Sparkles } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import type { QuoteLine } from '../api/types'
import { AiData } from '../ui/ai-data'
import { AiProgress, ScanReel, type ScanItem } from '../ui/ai-motion'
import { useScan } from '../ui/ai-motion-state'
import { formatQuoteDate } from '../ui/format'
import { aiReviewQueryKey, requestAiReview, type AiReviewFinding, type QuoteAiReview } from './review-api'

type Phase = AiReviewFinding['phase']
type Kind = AiReviewFinding['kind']

const phaseLabels: Record<Phase, string> = {
  customer: 'customer',
  equipment: 'equipment',
  pricing: 'pricing',
  shipping: 'shipping',
  services: 'services',
  tax: 'tax',
  financing: 'payment',
}

const filters: ReadonlyArray<readonly ['all' | Kind, string]> = [
  ['all', 'All'],
  ['adverse', 'Risks'],
  ['missing', 'Missing facts'],
  ['confirmed', 'Confirmed'],
]

function retryMessage(error: unknown) {
  return error instanceof Error && 'status' in error && (error as { status: number }).status === 412
    ? 'Quote changed. Reload the current quote to review its latest prices and selections.'
    : 'Insights could not load. Try again.'
}

/** Starts the review of this revision, then polls every half second while the ranking runs (demo). */
function useAiReview(quoteId: string, revision: number, enabled: boolean) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const key = aiReviewQueryKey(quoteId, revision)
  return useQuery({
    queryKey: key,
    enabled,
    staleTime: Infinity,
    retry: false,
    queryFn: ({ signal }) =>
      requestAiReview(api, quoteId, revision, {
        signal,
        poll: client.getQueryData<QuoteAiReview>(key)?.status === 'running',
      }),
    refetchInterval: (query) => (enabled && query.state.data?.status === 'running' && !query.state.error ? 500 : false),
  })
}

function FactsScan({ lines }: { lines: QuoteLine[] }) {
  const items: ScanItem[] = lines
    .filter((line) => !line.parent_line_id)
    .map((line) => ({
      key: line.id,
      label: [line.manufacturer, line.model].filter(Boolean).join(' ') || line.description,
      detail: line.line_type === 'service' ? 'Services' : line.taxonomy_label,
    }))
  const index = useScan(items.length, 170, true)
  const parked = items.length < 2 || index === items.length - 1
  return (
    <section aria-live="polite" className="overflow-hidden rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Sparkles aria-hidden="true" className="size-4 animate-pulse text-violet-600 motion-reduce:animate-none" />
          Reviewing current quote
        </h2>
        <span className="text-xs text-muted-foreground" role="status">
          {parked || !items.length ? 'Building findings' : `Checking ${items.length} items`}
        </span>
      </div>
      <AiProgress from={0.04} seconds={3} to={0.42} />
      {items.length ? (
        <div className="px-4 py-3">
          <ScanReel checks index={index} items={items} />
        </div>
      ) : null}
    </section>
  )
}

function FindingsScan({ findings }: { findings: AiReviewFinding[] }) {
  const items = findings.map((item) => ({
    key: item.key,
    label: item.title,
    detail: item.product_label,
    tone: item.kind,
  }))
  // High-reasoning ranking takes about ten seconds: one unhurried pass over the findings, then another.
  const index = useScan(items.length, Math.max(300, Math.min(900, 10_000 / Math.max(1, items.length))), true, true)
  return (
    <AiData className="px-1 py-1" label="AI quote insights">
      <ScanReel checks={false} index={index} items={items} />
    </AiData>
  )
}

/** True for a moment after this view saw the ranking land. */
function useJustRanked(status: QuoteAiReview['status'] | undefined) {
  const [previous, setPrevious] = useState(status)
  const [ranked, setRanked] = useState(false)
  if (status !== previous) {
    setPrevious(status)
    if (previous === 'running' && status === 'ready') setRanked(true)
  }
  useEffect(() => {
    if (!ranked) return
    const timer = window.setTimeout(() => setRanked(false), 1200)
    return () => window.clearTimeout(timer)
  }, [ranked])
  return ranked
}

function StageLink({ quoteId, phase, children }: { quoteId: string; phase: Phase; children: ReactNode }) {
  return (
    <Link
      className="inline-flex items-center gap-1 rounded text-xs font-semibold text-foreground underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
      params={{ quoteId, stage: phase === 'pricing' ? 'equipment' : phase }}
      to="/quotes/$quoteId/workflow/$stage"
    >
      {children}
      <ArrowUpRight aria-hidden="true" className="size-3" />
    </Link>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-semibold tabular-nums">{value}</dd>
    </div>
  )
}

export function AiReview({
  quoteId,
  revision,
  lines,
  active,
  action,
}: {
  quoteId: string
  revision: number
  lines: QuoteLine[]
  active: boolean
  /** Sales's approval of the review, at the right of the header (USA-1333). */
  action?: ReactNode
}) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const query = useAiReview(quoteId, revision, active)
  const [filter, setFilter] = useState<'all' | Kind>('all')
  const [allFindings, setAllFindings] = useState(false)
  const retry = useMutation({
    mutationFn: () => requestAiReview(api, quoteId, revision, { retry: true }),
    onSuccess: (result) => client.setQueryData(aiReviewQueryKey(result.quote_id, result.revision), result),
  })
  const review = query.data?.revision === revision ? query.data : undefined
  const justRanked = useJustRanked(review?.status)

  if (!review && !query.isError) return <FactsScan lines={lines} />
  if (!review)
    return (
      <section aria-live="polite" className="rounded-xl border bg-card p-5">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Sparkles aria-hidden="true" className="size-4 text-violet-600" />
          Insights unavailable
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{retryMessage(query.error)}</p>
        <Button className="mt-3" onClick={() => void query.refetch()} size="sm" variant="outline">
          Try again
        </Button>
      </section>
    )

  const ranking = review.status === 'running' && !query.isError
  const metrics = review.metrics
  const order = (key: string) => {
    const position = review.priorities.indexOf(key)
    return position < 0 ? 999 : position
  }
  const ranked = [...review.findings].sort((a, b) => order(a.key) - order(b.key))
  const visible = ranked.filter((item) => filter === 'all' || item.kind === filter)
  const adverse = review.findings.filter((item) => item.kind === 'adverse').length
  const missing = review.findings.filter((item) => item.kind === 'missing').length
  const leadTotal = review.lead_times.reduce((sum, item) => sum + item.count, 0)

  return (
    <section
      aria-labelledby="quote-ai-review-title"
      className="overflow-hidden rounded-xl border bg-card"
      data-approval-ai-review={review.status}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Sparkles aria-hidden="true" className="size-4 text-violet-600" />
          <h2 className="text-base font-semibold" id="quote-ai-review-title">
            AI insights
          </h2>
          {ranking ? null : (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 font-medium',
                adverse
                  ? 'bg-red-50 text-red-800 dark:bg-red-950/50 dark:text-red-200'
                  : 'bg-amber-50 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200',
              )}
            >
              {adverse ? `${adverse} risks` : `${missing} facts to confirm`}
            </span>
          )}
          <span className="text-muted-foreground">
            {metrics.ready_phases}/{metrics.total_phases} stages ready
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span aria-live="polite" className="text-xs text-muted-foreground">
            {ranking
              ? `Ranking ${review.findings.length} findings`
              : review.status === 'ready'
                ? `Ranked for this revision${review.completed_at ? ` · ${new Date(review.completed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}`
                : 'Quote facts'}
          </span>
          {action}
        </div>
      </div>
      {ranking ? (
        <AiProgress from={0.42} seconds={12} to={0.94} />
      ) : justRanked ? (
        <AiProgress done from={0.94} seconds={0.4} to={1} />
      ) : null}
      {review.status === 'failed' || query.isError ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 border-b bg-amber-50 px-4 py-2 dark:bg-amber-950/30"
          role="status"
        >
          <p className="text-xs text-amber-900 dark:text-amber-200">
            {query.isError ? retryMessage(query.error) : 'AI ranking is unavailable. The quote facts are below.'}
          </p>
          {review.can_retry ? (
            <LoadingButton
              loading={retry.isPending}
              loadingText="Retrying..."
              onClick={() => retry.mutate()}
              size="sm"
              variant="outline"
            >
              <RefreshCw aria-hidden="true" />
              Retry AI review
            </LoadingButton>
          ) : null}
          {retry.isError ? <p className="w-full text-xs text-destructive">{retryMessage(retry.error)}</p> : null}
        </div>
      ) : null}

      <section aria-label="Customer and project" className="border-b px-4 py-3">
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">{review.project.customer_name}</h3>
            {review.project.customer_notes ? (
              <p className="mt-1 text-sm break-words text-muted-foreground">{review.project.customer_notes}</p>
            ) : null}
            {review.project.requirements !== 'Not recorded' ? (
              <p className="mt-1 text-sm break-words text-muted-foreground">{review.project.requirements}</p>
            ) : null}
          </div>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            {(
              [
                ['Quote due', review.project.quote_due_date],
                ['Opening', review.project.opening_date],
                ['Latest delivery', review.project.latest_delivery_date],
              ] as const
            ).map(([label, value]) => (
              <div className="min-w-0" key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-1 font-medium">{value ? formatQuoteDate(value) : 'Not set'}</dd>
              </div>
            ))}
          </dl>
        </div>
        {review.project.ai_insights.length ? (
          <AiData className="mt-3 px-3 py-2" label="AI customer insights">
            <ul className="space-y-1 text-sm text-muted-foreground">
              {review.project.ai_insights.map((insight) => (
                <li key={insight}>{insight}</li>
              ))}
            </ul>
          </AiData>
        ) : null}
      </section>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-b px-4 py-3 sm:grid-cols-4">
        <Metric label="Sell" value={formatMoney(metrics.sell)} />
        <Metric label="Net" value={formatMoney(metrics.net)} />
        <Metric label="Profit" value={formatMoney(metrics.profit)} />
        <Metric label="Markup" value={metrics.markup == null ? '—' : formatPercent(metrics.markup)} />
      </dl>

      <div className="grid lg:grid-cols-[minmax(0,1.5fr)_minmax(240px,1fr)]">
        <div className="min-w-0 px-4 py-3">
          <h3 className="mb-3 text-sm font-semibold">Findings</h3>
          {ranking ? (
            <FindingsScan findings={ranked} />
          ) : (
            <>
              <div aria-label="Finding filters" className="mb-3 flex flex-wrap gap-1" role="group">
                {filters.map(([key, label]) => (
                  <button
                    aria-pressed={filter === key}
                    className={cn(
                      'min-h-8 rounded-md border px-2 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-ring',
                      filter === key
                        ? 'border-foreground bg-foreground text-background'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                    key={key}
                    onClick={() => setFilter(key)}
                    type="button"
                  >
                    {label} {key === 'all' ? ranked.length : ranked.filter((item) => item.kind === key).length}
                  </button>
                ))}
              </div>
              <AiData className="divide-y px-3" label="AI quote insights">
                {(allFindings ? visible : visible.slice(0, 8)).map((item, position) => (
                  <details
                    className={cn(
                      'group py-2.5',
                      justRanked && 'animate-[qb-ai-enter_420ms_cubic-bezier(0.16,1,0.3,1)_both]',
                    )}
                    key={item.key}
                    style={
                      justRanked
                        ? {
                            animationDelay: `${Math.min(position, 8) * 40}ms`,
                          }
                        : undefined
                    }
                  >
                    <summary className="flex cursor-pointer list-none items-start gap-2 rounded focus-visible:outline-2 focus-visible:outline-ring">
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-1.5 size-1.5 shrink-0 rounded-full',
                          item.kind === 'adverse'
                            ? 'bg-destructive'
                            : item.kind === 'confirmed'
                              ? 'bg-emerald-600'
                              : 'bg-amber-500',
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{item.title}</span>
                        {item.product_label ? (
                          <span className="text-xs text-muted-foreground">{item.product_label}</span>
                        ) : null}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {item.kind === 'missing' ? 'Confirm' : item.kind === 'adverse' ? 'Risk' : 'Known'}
                      </span>
                      <ChevronDown
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-muted-foreground group-open:rotate-180"
                      />
                    </summary>
                    <div className="mt-2 ml-3.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p className="w-full max-w-prose text-sm leading-relaxed text-muted-foreground">{item.detail}</p>
                      <StageLink phase={item.phase} quoteId={quoteId}>
                        Review {phaseLabels[item.phase]}
                      </StageLink>
                      {item.product_uuid ? (
                        <Link
                          className="text-xs font-semibold underline-offset-4 hover:underline"
                          params={{ productId: item.product_uuid }}
                          to="/products/$productId"
                        >
                          Product specs
                        </Link>
                      ) : null}
                    </div>
                  </details>
                ))}
                {!visible.length ? (
                  <p className="py-6 text-sm text-muted-foreground">No findings in this group.</p>
                ) : null}
              </AiData>
              {visible.length > 8 ? (
                <Button className="mt-2" onClick={() => setAllFindings(!allFindings)} size="sm" variant="ghost">
                  {allFindings ? 'Show top findings' : `Show all ${visible.length} findings`}
                </Button>
              ) : null}
            </>
          )}
        </div>
        <aside className="min-w-0 border-t px-4 py-3 lg:border-t-0 lg:border-l">
          <h3 className="text-sm font-semibold">Lead times</h3>
          <AiData className="mt-3 space-y-3 p-3" label="AI planning lead times">
            {review.lead_times.map((item) => (
              <details key={item.label}>
                <summary className="cursor-pointer list-none rounded focus-visible:outline-2 focus-visible:outline-ring">
                  <span className="flex justify-between text-sm">
                    <span>{item.label}</span>
                    <span className="font-semibold tabular-nums">{item.count}</span>
                  </span>
                  <span className="mt-1.5 block h-2 rounded-full bg-muted">
                    <span
                      className={cn(
                        'block h-full rounded-full',
                        item.label === 'Over 4 weeks' ? 'bg-amber-500' : 'bg-foreground/70',
                      )}
                      style={{
                        width: `${(item.count / Math.max(1, leadTotal)) * 100}%`,
                      }}
                    />
                  </span>
                </summary>
                <p className="mt-2 text-xs text-muted-foreground">{item.products.join(', ')}</p>
              </details>
            ))}
            {!leadTotal ? <p className="text-sm text-muted-foreground">No catalog lead times recorded.</p> : null}
          </AiData>
          <dl className="mt-5 space-y-2 border-t pt-3 text-sm">
            {(
              [
                ['Freight', formatMoney(metrics.freight)],
                ['Services', formatMoney(metrics.services)],
                ['Tax', formatMoney(metrics.tax)],
                ['Total', formatMoney(metrics.total)],
              ] as const
            ).map(([label, value]) => (
              <div className="flex justify-between gap-3" key={label}>
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium tabular-nums">{value}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </div>
    </section>
  )
}
